/**
 * C5 · Per-exercise accuracy scoring: compare a recorded pitch trace against
 * the rendered target timeline. Scope is strictly pitch + timing (J6) — the
 * score never comments on tone, technique, or health.
 */
import { targetMidiAt } from './patterns.js';
const FULL_CREDIT_CENTS = 35; // within this = perfect for an untrained voice
const ZERO_CREDIT_CENTS = 150; // beyond this = missed note
/**
 * Octave-forgiveness: students (especially untrained, J1) often sing the
 * right pitch class an octave off from the reference. We score against the
 * nearest octave of the target but track it for coverage honesty.
 */
function nearestOctaveDelta(sungMidi, targetMidi) {
    let d = sungMidi - targetMidi;
    while (d > 6)
        d -= 12;
    while (d < -6)
        d += 12;
    return d;
}
export function scoreTrace(trace, rendered) {
    let pitchedTargetSamples = 0;
    let sungSamples = 0;
    let centsSum = 0;
    let creditSum = 0;
    const noteAgg = rendered.notes
        .filter((n) => n.midi !== null)
        .map((n) => ({ startSec: n.startSec, targetMidi: n.midi, cents: [] }));
    // Sample target on a fixed grid so coverage is trace-rate independent.
    const dt = 0.05;
    const traceSorted = [...trace].sort((a, b) => a.t - b.t);
    let ti = 0;
    for (let t = 0; t < rendered.totalSec; t += dt) {
        const target = targetMidiAt(rendered, t);
        if (target === null)
            continue;
        pitchedTargetSamples++;
        // nearest trace point within 60ms
        while (ti < traceSorted.length - 1 && traceSorted[ti + 1].t <= t)
            ti++;
        const candidates = [traceSorted[ti], traceSorted[ti + 1]].filter((p) => !!p && Math.abs(p.t - t) <= 0.06);
        if (candidates.length === 0)
            continue;
        const p = candidates.reduce((a, b) => (Math.abs(a.t - t) <= Math.abs(b.t - t) ? a : b));
        sungSamples++;
        // Deviation is measured against the target at the sample's own timestamp,
        // so a note boundary between grid point and sample doesn't count as error.
        const targetAtSample = targetMidiAt(rendered, p.t) ?? target;
        const cents = Math.abs(nearestOctaveDelta(p.midi, targetAtSample) * 100);
        centsSum += cents;
        const credit = cents <= FULL_CREDIT_CENTS
            ? 1
            : cents >= ZERO_CREDIT_CENTS
                ? 0
                : 1 - (cents - FULL_CREDIT_CENTS) / (ZERO_CREDIT_CENTS - FULL_CREDIT_CENTS);
        creditSum += credit;
        // attribute to the containing note
        const note = noteAgg.findLast((n) => n.startSec <= t);
        if (note)
            note.cents.push(nearestOctaveDelta(p.midi, note.targetMidi) * 100);
    }
    const coverage = pitchedTargetSamples > 0 ? sungSamples / pitchedTargetSamples : 0;
    const pitchQuality = sungSamples > 0 ? creditSum / sungSamples : 0;
    // Timing (coverage) and pitch quality both matter; weight pitch higher.
    const score = Math.round(100 * (0.7 * pitchQuality * coverage + 0.3 * coverage));
    return {
        score: Math.max(0, Math.min(100, score)),
        coverage,
        meanAbsCents: sungSamples > 0 ? centsSum / sungSamples : 0,
        perNote: noteAgg.map((n) => ({
            startSec: n.startSec,
            targetMidi: n.targetMidi,
            avgCents: n.cents.length > 0 ? n.cents.reduce((a, b) => a + b, 0) / n.cents.length : null,
        })),
    };
}
//# sourceMappingURL=scoring.js.map